import { useState } from "react";
import { Users, Plus, UserPlus, X, Copy, Check, Loader2, LogOut, ChevronRight } from "lucide-react";
import GlassCard from "./GlassCard";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useCommunityGroups } from "@/hooks/useCommunityGroups";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const CommunityGroupsCard = () => {
    const { groups, loading, createGroup, joinGroup, leaveGroup } = useCommunityGroups();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isJoinOpen, setIsJoinOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [processing, setProcessing] = useState(false);
    const [copiedCode, setCopiedCode] = useState(null);

    const handleCreate = async () => {
        if (!newGroupName.trim()) return;
        setProcessing(true);
        const success = await createGroup(newGroupName.trim());
        setProcessing(false);
        if (success) {
            setIsCreateOpen(false);
            setNewGroupName("");
        }
    };

    const handleJoin = async () => {
        if (!inviteCode.trim()) return;
        // Fix: leftover no-op line (`setProcessing(processing)`) left behind
        // mid-edit — it did nothing (set state to its own current value) but
        // sat here as dead code with a stale "Fix typo" comment referring to
        // the very next line, which already did the real fix correctly.
        setProcessing(true);
        const success = await joinGroup(inviteCode.trim());
        setProcessing(false);
        if (success) {
            setIsJoinOpen(false);
            setInviteCode("");
        }
    };

    const copyInviteCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        toast.success("Invite code copied to clipboard!");
        setTimeout(() => setCopiedCode(null), 2000);
    };

    if (loading) {
        return (
            <GlassCard className="p-6 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </GlassCard>
        );
    }

    return (
        <section className="space-y-6">
            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold tracking-tight text-foreground">Community Groups</h2>
                </div>
                <p className="text-sm text-muted-foreground">
                    Connect with your neighborhood to share trusted service providers.
                </p>
                <div className="flex gap-2 mt-2">
                    <Button
                        size="sm"
                        variant="soft"
                        onClick={() => setIsJoinOpen(true)}
                        className="flex-1 rounded-2xl h-10 text-xs gap-1.5 bg-primary/5 hover:bg-primary/10 border-none"
                    >
                        <UserPlus className="w-3.5 h-3.5" /> Join Group
                    </Button>
                    <Button
                        size="sm"
                        variant="soft"
                        onClick={() => setIsCreateOpen(true)}
                        className="flex-1 rounded-2xl h-10 text-xs gap-1.5 bg-primary/5 hover:bg-primary/10 border-none"
                    >
                        <Plus className="w-3.5 h-3.5" /> Create Group
                    </Button>
                </div>
            </div>

            {groups.length === 0 ? (
                <GlassCard className="p-8 text-center space-y-4 bg-primary/5 border-dashed border-2 border-primary/20 rounded-[2rem]">
                    <div className="relative mx-auto w-16 h-16">
                        <div className="absolute inset-0 bg-primary/10 rounded-2xl rotate-6" />
                        <div className="absolute inset-0 bg-primary/20 rounded-2xl -rotate-3 transition-transform group-hover:rotate-0" />
                        <div className="relative inset-0 bg-white rounded-2xl shadow-sm border border-primary/10 flex items-center justify-center">
                            <Users className="w-8 h-8 text-primary" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <p className="text-base font-semibold text-foreground">No groups yet</p>
                        <p className="text-sm text-muted-foreground">
                            Join your neighborhood group to see who your neighbors trust.
                        </p>
                    </div>
                    <Button
                        variant="link"
                        className="text-primary font-bold"
                        onClick={() => setIsJoinOpen(true)}
                    >
                        Have an invite code?
                    </Button>
                </GlassCard>
            ) : (
                <div className="grid gap-3">
                    {groups.map((group) => (
                        <GlassCard key={group.id} className="p-0 overflow-hidden relative group border-none bg-white/40 hover:bg-white/60 transition-all duration-300 rounded-3xl">
                            <div className="p-5 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center shrink-0">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-foreground truncate text-base">{group.name}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <div
                                                className="flex items-center gap-1.5 bg-primary/5 px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer hover:bg-primary/10 transition-colors border border-primary/10"
                                                onClick={() => copyInviteCode(group.invite_code)}
                                            >
                                                {copiedCode === group.invite_code ? (
                                                    <Check className="w-3 h-3 text-emerald-500" />
                                                ) : (
                                                    <Copy className="w-3 h-3 text-primary/60" />
                                                )}
                                                <span className="uppercase tracking-[0.1em] text-primary/80">Code: {group.invite_code}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-10 w-10 text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-2xl"
                                        onClick={() => leaveGroup(group.id)}
                                    >
                                        <LogOut className="w-4 h-4" />
                                    </Button>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground/30" />
                                </div>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            )}

            {/* Create Group Dialog */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="rounded-2xl w-[90%] max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Create Community Group</DialogTitle>
                        <DialogDescription>
                            Create a group for your street or suburb and share the code with neighbors.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="group-name">Group Name</Label>
                            <Input
                                id="group-name"
                                placeholder="e.g. Table View Community"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                className="rounded-xl"
                            />
                        </div>
                        <Button
                            className="w-full rounded-xl"
                            onClick={handleCreate}
                            disabled={processing || !newGroupName.trim()}
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                            Create Group
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Join Group Dialog */}
            <Dialog open={isJoinOpen} onOpenChange={setIsJoinOpen}>
                <DialogContent className="rounded-2xl w-[90%] max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Join Community Group</DialogTitle>
                        <DialogDescription>
                            Enter the 6-digit invite code shared by your neighbor.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="invite-code">Invite Code</Label>
                            <Input
                                id="invite-code"
                                placeholder="ENTER CODE"
                                value={inviteCode}
                                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                className="rounded-xl uppercase tracking-widest text-center text-lg font-bold"
                                maxLength={6}
                            />
                        </div>
                        <Button
                            className="w-full rounded-xl"
                            onClick={handleJoin}
                            disabled={processing || inviteCode.length < 6}
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                            Join Group
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
};

export default CommunityGroupsCard;
